"use client";

import { useState, useEffect } from "react";
import {
  Button,
  Table,
  Tag,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  Spin,
  message,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import useTicketsStore, { Ticket } from "./TicketsStore.ts";
import {
  TICKET_TYPE_CONFIG,
  TICKET_STATUS_CONFIG,
  type TicketStatus,
} from "./TicketEnums.ts";

const { TextArea } = Input;

export default function Tickets() {
  const {
    getTickets,
    getTicketById,
    updateStatus,
    deleteTicket,
    ticketsData,
    ticketDetail,
    isListLoading,
    isDetailLoading,
    isSaving,
  } = useTicketsStore();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [typeFilter, setTypeFilter] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | undefined>(
    undefined
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [statusForm] = Form.useForm();

  useEffect(() => {
    fetchTickets();
  }, [currentPage, pageSize, typeFilter, statusFilter]);

  const fetchTickets = () => {
    const params: any = {
      page: currentPage,
      per_page: pageSize,
      type: typeFilter,
      status: statusFilter,
    };
    getTickets(params);
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  const handleView = async (id: number) => {
    const res = await getTicketById(id);
    if (res.success && res.data) {
      setIsDrawerOpen(true);
      statusForm.setFieldsValue({
        status: res.data.status,
        admin_notes: res.data.admin_notes || "",
      });
    } else {
      message.error("Không thể tải chi tiết ticket");
    }
  };

  const handleDelete = async (id: number) => {
    const res = await deleteTicket(id);
    if (res.success) {
      message.success("Đã xóa ticket");
      fetchTickets();
    }
  };

  const handleUpdateStatus = async () => {
    const values = await statusForm.validateFields();
    if (!ticketDetail) return;
    const res = await updateStatus(
      ticketDetail.id,
      values.status,
      values.admin_notes
    );
    if (res.success) {
      message.success("Cập nhật trạng thái thành công");
      fetchTickets();
      setIsDrawerOpen(false);
    }
  };

  const columns: ColumnsType<Ticket> = [
    {
      title: (
        <span className="flex items-center gap-2">
          <i className="fa-solid fa-ticket text-black"></i>
          <span className="font-semibold">Ticket</span>
        </span>
      ),
      key: "ticket",
      render: (_, record) => {
        const typeCfg = TICKET_TYPE_CONFIG[record.type];
        return (
          <div>
            <div className="font-semibold text-gray-900">{record.subject}</div>
            <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
              {typeCfg && (
                <Tag
                  color={typeCfg.color}
                  className="rounded-full px-3 py-0.5 border-0 flex items-center gap-1"
                >
                  <i className={`fa-solid ${typeCfg.icon}`}></i>
                  {typeCfg.label}
                </Tag>
              )}
              <span>
                {new Date(record.created_at).toLocaleString("vi-VN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <i className="fa-solid fa-user text-black"></i>
          <span className="font-semibold">User</span>
        </span>
      ),
      key: "user",
      render: (_, record) =>
        // @ts-expect-error user may be attached from API
        record.user ? (
          <div className="space-y-1 text-sm">
            {/* @ts-expect-error user may be attached from API */}
            <div className="font-medium text-gray-900">{record.user.name}</div>
            <div className="text-xs text-gray-500">ID: {record.user_id}</div>
          </div>
        ) : (
          <Tag>Unknown</Tag>
        ),
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <i className="fa-solid fa-circle-check text-black"></i>
          <span className="font-semibold">Status</span>
        </span>
      ),
      key: "status",
      render: (_, record) => {
        const cfg = TICKET_STATUS_CONFIG[record.status];
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: (
        <span className="flex items-center gap-2">
          <i className="fa-solid fa-user-shield text-black"></i>
          <span className="font-semibold">Assigned Admin</span>
        </span>
      ),
      key: "admin",
      render: (_, record) =>
        record.admin ? (
          <Tag color="purple">{record.admin.name}</Tag>
        ) : (
          <Tag color="default">Unassigned</Tag>
        ),
    },
    {
      title: <span className="font-semibold">Actions</span>,
      key: "action",
      align: "center",
      width: 160,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
            icon={<i className="fa-solid fa-eye" />}
            onClick={() => handleView(record.id)}
          />
          <Popconfirm
            title="Xóa ticket này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              className="hover:bg-red-50"
              icon={<i className="fa-solid fa-trash" />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const paginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: Array.isArray(ticketsData) ? ticketsData.length : 0,
    showSizeChanger: true,
    showTotal: (total: number, range: [number, number]) =>
      `${range[0]}-${range[1]} of ${total} tickets`,
  };

  return (
    <>
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 font-medium">
          <span>Home</span>
          <span>/</span>
          <span className="text-black">Tickets</span>
        </div>
        <h1 className="text-3xl font-bold text-black">Ticket Management</h1>
      </div>

      <div className="bg-white rounded-3xl shadow-soft border border-gray-100 overflow-hidden">
        <div className="p-6">
          <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <Select
                placeholder="Type"
                allowClear
                style={{ width: 200 }}
                value={typeFilter}
                onChange={(val) => setTypeFilter(val)}
              >
                {Object.entries(TICKET_TYPE_CONFIG).map(([value, cfg]) => (
                  <Select.Option key={value} value={Number(value)}>
                    <i className={`fa-solid ${cfg.icon} mr-2`} />
                    {cfg.label}
                  </Select.Option>
                ))}
              </Select>

              <Select
                placeholder="Status"
                allowClear
                style={{ width: 180 }}
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
              >
                {Object.entries(TICKET_STATUS_CONFIG).map(([key, cfg]) => (
                  <Select.Option key={key} value={key}>
                    {cfg.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          <Table
            columns={columns}
            dataSource={Array.isArray(ticketsData) ? ticketsData : []}
            loading={isListLoading}
            rowKey="id"
            pagination={paginationConfig}
            onChange={handleTableChange}
            scroll={{ x: 900 }}
          />
        </div>
      </div>

      <Drawer
        title={
          <span className="flex items-center gap-2 text-lg">
            <i className="fa-solid fa-ticket text-black"></i>
            Ticket Detail
          </span>
        }
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        width={600}
        destroyOnClose
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsDrawerOpen(false)}>Đóng</Button>
            <Button
              type="primary"
              onClick={handleUpdateStatus}
              loading={isSaving}
            >
              Cập nhật
            </Button>
          </div>
        }
      >
        {isDetailLoading || !ticketDetail ? (
          <div className="flex justify-center items-center h-40">
            <Spin tip="Đang tải..." />
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h3 className="text-lg font-bold mb-1">{ticketDetail.subject}</h3>
              {ticketDetail.description && (
                <p className="text-sm text-gray-600 mb-2">
                  {ticketDetail.description}
                </p>
              )}
            </div>

            <Form form={statusForm} layout="vertical">
              <Form.Item
                name="status"
                label="Trạng thái"
                rules={[{ required: true, message: "Vui lòng chọn trạng thái" }]}
              >
                <Select>
                  {Object.entries(TICKET_STATUS_CONFIG).map(([key, cfg]) => (
                    <Select.Option key={key} value={key}>
                      {cfg.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="admin_notes" label="Ghi chú admin">
                <TextArea rows={4} placeholder="Nhập ghi chú xử lý..." />
              </Form.Item>
            </Form>
          </>
        )}
      </Drawer>
    </>
  );
}


